import { FormEvent, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Account, financialService, SalaryAdvance } from "../../services/financialService";
import { todayIso } from "../../utils/dates";

const money = (value: string | number) => `UGX ${Number(value).toLocaleString()}`;

const STAGE_LABELS: Record<SalaryAdvance["stage"], string> = {
  awaiting_approval: "Waiting for approval",
  approved: "Approved, not paid out",
  payout_pending: "Payout waiting for approval",
  paid_out: "Paid out, recovered by next payroll",
  recovered: "Recovered",
  rejected: "Rejected",
  cancelled: "Withdrawn",
};

function errorText(failure: unknown) {
  const data = (failure as { response?: { data?: unknown } })?.response?.data;
  if (!data || typeof data !== "object") return "That could not be completed.";
  return Object.values(data as Record<string, unknown>).flat().map(String).join(" ");
}

/**
 * Finance's view of salary advances: decide requests, pay out approved ones,
 * and see what the next payroll run will take back.
 */
export default function SalaryAdvancesPanel({
  accounts,
  isSelectable,
  canInitiate,
  canReview,
  canApprove,
  userId,
}: {
  accounts: Account[] | undefined;
  isSelectable: (account: Account) => boolean;
  canInitiate: boolean;
  canReview: boolean;
  canApprove: boolean;
  userId?: number;
}) {
  const queryClient = useQueryClient();
  const [error, setError] = useState("");
  const [payingOut, setPayingOut] = useState<number | null>(null);
  const advances = useQuery({
    queryKey: ["salary-advances"],
    queryFn: financialService.getSalaryAdvances,
  });
  const onDone = {
    onSuccess: () => {
      setError("");
      queryClient.invalidateQueries({ queryKey: ["salary-advances"] });
    },
    onError: (failure: unknown) => setError(errorText(failure)),
  };
  const approve = useMutation({ mutationFn: financialService.approveSalaryAdvance, ...onDone });
  const reject = useMutation({ mutationFn: financialService.rejectSalaryAdvance, ...onDone });
  const cancel = useMutation({ mutationFn: financialService.cancelSalaryAdvance, ...onDone });
  const payOut = useMutation({ mutationFn: financialService.payOutSalaryAdvance, ...onDone });
  const verifyPayout = useMutation({ mutationFn: financialService.verifyAdvancePayout, ...onDone });
  const approvePayout = useMutation({ mutationFn: financialService.approveAdvancePayout, ...onDone });

  const rows = advances.data ?? [];
  const submitPayout = (event: FormEvent<HTMLFormElement>, advance: SalaryAdvance) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    payOut.mutate(
      {
        id: advance.id,
        payment_account: Number(form.get("account")),
        reference: String(form.get("reference")).trim(),
        payment_date: String(form.get("date") || "") || undefined,
      },
      { onSuccess: () => setPayingOut(null) },
    );
  };

  return (
    <div className="card">
      <div className="card-content">
        <h3 className="font-medium text-gray-900">Salary advances</h3>
        <p className="mt-0.5 text-sm text-gray-500">
          Staff ask from their profile page, up to 40% of their take-home pay. Approving an
          advance records what they owe and what the school owes them; paying it out is a
          separate payment with its own approval. The next payroll run takes the whole advance
          back.
        </p>
        {error && <div className="mt-2 rounded bg-red-50 p-3 text-sm text-red-700">{error}</div>}
        {rows.length === 0 && (
          <p className="mt-3 text-sm text-gray-500">No salary advances yet.</p>
        )}
        <ul className="mt-3 divide-y">
          {rows.map((advance) => {
            const payout = advance.disbursements.find((d) =>
              ["submitted", "verified", "returned"].includes(d.workflow_status || ""),
            );
            // The engine refuses to let anyone sign what they raised; hide the button to match.
            const raisedByMe = advance.initiated_by === userId;
            return (
              <li key={advance.id} className="py-3">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="font-medium">
                      {advance.staff_name} · {money(advance.amount)}
                    </div>
                    <div className="text-xs text-gray-500">
                      {advance.employee_id} · take-home {money(advance.net_pay_basis)} ·{" "}
                      {advance.reason}
                    </div>
                    {payout && (
                      <div className="text-xs text-amber-800">
                        Payout {payout.voucher_number} from {payout.payment_account_name} ·
                        reference {payout.reference} · {payout.workflow_status}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-3 text-sm">
                    <span className="rounded bg-gray-100 px-2 py-1 text-xs">
                      {STAGE_LABELS[advance.stage]}
                    </span>
                    {advance.stage === "awaiting_approval" && canApprove && !raisedByMe && (
                      <>
                        <button className="text-green-700" onClick={() => approve.mutate({ id: advance.id })}>
                          Approve
                        </button>
                        <button
                          className="text-red-700"
                          onClick={() => {
                            const reason = window.prompt("Why is this advance turned down?")?.trim();
                            if (reason) reject.mutate({ id: advance.id, reason });
                          }}
                        >
                          Reject
                        </button>
                      </>
                    )}
                    {advance.stage === "approved" && canInitiate && (
                      <>
                        <button className="text-primary-700" onClick={() => setPayingOut(advance.id)}>
                          Pay out
                        </button>
                        <button className="text-red-700" onClick={() => cancel.mutate({ id: advance.id })}>
                          Withdraw
                        </button>
                      </>
                    )}
                    {payout?.workflow_status === "submitted" && canReview && payout.initiated_by !== userId && (
                      <button className="font-medium text-primary-700 hover:underline" onClick={() => verifyPayout.mutate({ id: payout.id })}>
                        Mark payout reviewed
                      </button>
                    )}
                    {payout?.workflow_status === "verified" && canApprove && payout.initiated_by !== userId && (
                      <button className="btn btn-primary" onClick={() => approvePayout.mutate({ id: payout.id })}>
                        Approve &amp; pay
                      </button>
                    )}
                  </div>
                </div>
                {payingOut === advance.id && (
                  <form
                    onSubmit={(event) => submitPayout(event, advance)}
                    className="mt-2 grid gap-2 rounded-md border border-gray-200 bg-gray-50 p-3 md:grid-cols-4"
                  >
                    <select required name="account" className="input md:col-span-2" defaultValue="">
                      <option value="" disabled>Paid from</option>
                      {(accounts ?? []).filter(isSelectable).map((account) => (
                        <option key={account.id} value={account.id}>
                          {account.code} · {account.name}
                        </option>
                      ))}
                    </select>
                    <input required name="reference" className="input" placeholder="Bank or cash reference" />
                    <input name="date" type="date" max={todayIso()} className="input" />
                    <div className="flex gap-2 md:col-span-4">
                      <button className="btn btn-primary" disabled={payOut.isLoading}>
                        Raise payout for approval
                      </button>
                      <button type="button" className="btn btn-outline" onClick={() => setPayingOut(null)}>
                        Cancel
                      </button>
                    </div>
                  </form>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
